import React, { useState } from 'react';
import { useWingmanStore } from '../store/useWingmanStore';
import { PaymentService } from '../services/paymentService';
import { motion, AnimatePresence } from 'framer-motion';
import { spatialCommandController } from '../spatial/spatialCommandController';
import { MonitorPlay, Lock, Hash, Sparkles, Search, Flame } from 'lucide-react';
import { CinematicMediaCard } from './premium/CinematicMediaCard';
import { VideoInsightCard } from './VideoInsightCard';

/** Vibe → color mapping for badge rendering */
const VIBE_COLORS: Record<string, string> = {
    nightlife: 'from-purple-500 to-pink-500',
    coastal: 'from-cyan-400 to-blue-500',
    luxury: 'from-amber-400 to-yellow-600',
    cultural: 'from-rose-400 to-red-500',
    historic: 'from-orange-400 to-amber-600',
    culinary: 'from-green-400 to-emerald-500',
    educational: 'from-blue-400 to-indigo-500',
    adventure: 'from-lime-400 to-green-500',
    calm: 'from-teal-300 to-cyan-400',
    exploration: 'from-violet-400 to-purple-500',
    urban: 'from-slate-400 to-gray-500',
    scenic: 'from-sky-400 to-blue-400',
    shopping: 'from-pink-400 to-rose-500',
};

export const InsightBubble: React.FC = () => {
    const activeInsight = useWingmanStore(state => state.activeInsight);
    const setInsight = useWingmanStore(state => state.setInsight);
    const clearInsight = useWingmanStore(state => state.clearInsight);
    const userTier = useWingmanStore(state => state.userTier);
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState<any>(null);

    if (!activeInsight) return null;
    const insight = activeInsight;

    const handleUpgrade = async () => {
        setIsUpgrading(true);
        try {
            const config = await PaymentService.getConfig();
            await PaymentService.createCheckoutSession({
                customerEmail: 'explorer@wingman.ai',
                priceId: config.proPriceId,
                userId: 'dev-user-id'
            });
        } catch (err) {
            console.error('[UPGRADE ERROR]', err);
            setIsUpgrading(false);
        }
    };

    const isLocked = (insight as any).monetization?.gate ?? (userTier === 'free' && insight.message?.includes("Locked"));

    return (
        <>
        <AnimatePresence mode="wait">
            <motion.div
                key={insight.message}
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.9 }}
                className="pointer-events-auto fixed bottom-0 left-0 right-0 z-[9999] w-full rounded-t-[2.5rem] border-t border-x border-white/20 bg-black/90 p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-[0_-10px_50px_rgba(0,0,0,0.5)] backdrop-blur-3xl md:bottom-auto md:right-6 md:top-24 md:left-auto md:w-[420px] md:rounded-3xl md:border md:pb-5"
            >
                <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-400">
                            <MonitorPlay className="h-4 w-4" />
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">Visual Intelligence</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {insight.vibe && (
                            <div className={`rounded-full bg-gradient-to-r ${VIBE_COLORS[insight.vibe.toLowerCase()] || 'from-gray-500 to-slate-600'} px-3 py-1 text-[10px] font-black uppercase tracking-tighter text-white`}>
                                {insight.vibe}
                            </div>
                        )}
                        <button onClick={() => clearInsight()} className="rounded-full p-1 text-white/30 hover:bg-white/10 hover:text-white transition-all duration-100 active:scale-95">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                </div>

                <div className="relative overflow-hidden">
                    {!isLocked && insight.videos && insight.videos.length > 0 ? (
                        <div className="mt-2 overflow-hidden rounded-xl border border-white/10 bg-black">
                             <CinematicMediaCard video={insight.videos[0]} isOpen={true} inline={true} onClose={() => {}} />
                        </div>
                    ) : (
                        <div className={isLocked ? "blur-md opacity-40" : ""}>
                            <p className="text-sm font-medium leading-relaxed text-white/90">{insight.insightSummary || insight.message}</p>
                        </div>
                    )}
                    {isLocked && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                            <Lock className="h-5 w-5 text-amber-400" />
                            <button onClick={handleUpgrade} disabled={isUpgrading} className="rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 px-4 py-1.5 text-[11px] font-black uppercase text-white active:scale-95 transition-all duration-100">
                                {isUpgrading ? 'Redirecting...' : 'Upgrade to Voyager'}
                            </button>
                        </div>
                    )}
                </div>

                {!isLocked && (
                    <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t border-white/5">
                        <button 
                            onClick={async () => {
                                console.log('[WINGMAN] Triggering Ask Video flow...');
                                useWingmanStore.getState().setChatOpen(true);
                                try {
                                    const { contextEngine } = await import('../context/contextEngine');
                                    await contextEngine.processUserMessage("What are the key highlights of this video?");
                                } catch (e) {
                                    console.error('[WINGMAN] Ask Video failed', e);
                                }
                            }}
                            className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-[9px] font-bold uppercase text-white/70 hover:bg-white/10 transition-all duration-100 active:scale-95"
                        >
                            <Sparkles className="h-3 w-3" /> Ask Video
                        </button>
                        <button onClick={() => useWingmanStore.getState().setVibeModeOpen(true)} className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-[9px] font-bold uppercase text-white/70 hover:bg-white/10 transition-all duration-100 active:scale-95">
                            <Flame className="h-3 w-3" /> Vibe Mode
                        </button>
                    </div>
                )}
            </motion.div>
        </AnimatePresence>
        </>
    );
};
